import { Injectable, computed, signal } from '@angular/core';
import IPixelMapDay from "@interfaces/pixel-map-day.interface";

import january2023Data from '@data/pixel-map/2023/01-january';
import february2023Data from '@data/pixel-map/2023/02-february';
import march2023Data from '@data/pixel-map/2023/03-march';
import april2023Data from '@data/pixel-map/2023/04-april';
import may2023Data from '@data/pixel-map/2023/05-may';
import june2023Data from '@data/pixel-map/2023/06-june';
import july2023Data from '@data/pixel-map/2023/07-july';
import august2023Data from '@data/pixel-map/2023/08-august';
import september2023Data from '@data/pixel-map/2023/09-september';
import october2023Data from '@data/pixel-map/2023/10-october';
import november2023Data from '@data/pixel-map/2023/11-november';
import december2023Data from '@data/pixel-map/2023/12-december';

import january2024Data from '@data/pixel-map/2024/01-january';
import february2024Data from '@data/pixel-map/2024/02-february';
import march2024Data from '@data/pixel-map/2024/03-march';
import april2024Data from '@data/pixel-map/2024/04-april';
import may2024Data from '@data/pixel-map/2024/05-may';
import june2024Data from '@data/pixel-map/2024/06-june';
import july2024Data from '@data/pixel-map/2024/07-july';
import august2024Data from '@data/pixel-map/2024/08-august';
import september2024Data from '@data/pixel-map/2024/09-september';
import october2024Data from '@data/pixel-map/2024/10-october';
import november2024Data from '@data/pixel-map/2024/11-november';
import december2024Data from '@data/pixel-map/2024/12-december';

import january2025Data from '@data/pixel-map/2025/01-january';
import february2025Data from '@data/pixel-map/2025/02-february';
import march2025Data from '@data/pixel-map/2025/03-march';
import april2025Data from '@data/pixel-map/2025/04-april';
import may2025Data from '@data/pixel-map/2025/05-may';
import june2025Data from '@data/pixel-map/2025/06-june';
import july2025Data from '@data/pixel-map/2025/07-july';
import august2025Data from '@data/pixel-map/2025/08-august';
import september2025Data from '@data/pixel-map/2025/09-september';
import october2025Data from '@data/pixel-map/2025/10-october';
import november2025Data from '@data/pixel-map/2025/11-november';
import december2025Data from '@data/pixel-map/2025/12-december';

import january2026Data from '@data/pixel-map/2026/01-january';
import february2026Data from '@data/pixel-map/2026/02-february';
import march2026Data from '@data/pixel-map/2026/03-march';
import april2026Data from '@data/pixel-map/2026/04-april';
import may2026Data from '@data/pixel-map/2026/05-may';
import june2026Data from '@data/pixel-map/2026/06-june';
import july2026Data from '@data/pixel-map/2026/07-july';
import august2026Data from '@data/pixel-map/2026/08-august';
import september2026Data from '@data/pixel-map/2026/09-september';
import october2026Data from '@data/pixel-map/2026/10-october';
import november2026Data from '@data/pixel-map/2026/11-november';
import december2026Data from '@data/pixel-map/2026/12-december';

import { AVAILABLE_YEARS, CALENDAR_MONTHS } from '@data/pixel-map/pixel-map-calendar';

@Injectable({
	providedIn: 'root',
})
export class CalendarDataService {
	readonly calendarMonths = CALENDAR_MONTHS;

	readonly availableYears = AVAILABLE_YEARS;
	private readonly days = Array.from({ length: 31 }, (_, i) => i);

	private readonly yearDataMap: Record<number, IPixelMapDay[][]> = {
		2023: [
			january2023Data,
			february2023Data,
			march2023Data,
			april2023Data,
			may2023Data,
			june2023Data,
			july2023Data,
			august2023Data,
			september2023Data,
			october2023Data,
			november2023Data,
			december2023Data
		],
		2024: [
			january2024Data,
			february2024Data,
			march2024Data,
			april2024Data,
			may2024Data,
			june2024Data,
			july2024Data,
			august2024Data,
			september2024Data,
			october2024Data,
			november2024Data,
			december2024Data
		],
		2025: [
			january2025Data,
			february2025Data,
			march2025Data,
			april2025Data,
			may2025Data,
			june2025Data,
			july2025Data,
			august2025Data,
			september2025Data,
			october2025Data,
			november2025Data,
			december2025Data
		],
		2026: [
			january2026Data,
			february2026Data,
			march2026Data,
			april2026Data,
			may2026Data,
			june2026Data,
			july2026Data,
			august2026Data,
			september2026Data,
			october2026Data,
			november2026Data,
			december2026Data
		],
	};

	private readonly monthsByYear = new Map<number, ReturnType<CalendarDataService['adjustFebruaryDays']>>(
		this.availableYears.map(y => [y, this.adjustFebruaryDays(y)])
	);

	private readonly initYear = (() => {
		const now = new Date().getFullYear();
		return this.availableYears.includes(now as any) ? now : this.availableYears[this.availableYears.length - 1];
	})();

	readonly selectedYear = signal<number>(this.initYear);

	/** Reactive calendar for the selected year. */
	readonly calendar = computed(() => {
		const year = this.selectedYear();
		const data = this.yearDataMap[year];
		const months = this.monthsByYear.get(year) ?? this.adjustFebruaryDays(year);

		return {
			days: this.days,
			months,
			data
		};
	});

	setSelectedYear(year: number): void {
		const next = Number(year);
		if (!this.yearDataMap[next]) return;
		this.selectedYear.set(next);
	}

	getCalendarData(year: number) {
		const data = this.yearDataMap[year];
		if (!data) throw new Error('Year data not available');
		const months = this.monthsByYear.get(year) ?? this.adjustFebruaryDays(year);
		return { months, days: data };
	}

	adjustFebruaryDays(year: number) {
		const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

		return this.calendarMonths.map(month => {
			if (month.name === 'February') {
				return {
					...month,
					days: isLeapYear ? 29 : 28
				};
			}
			return month;
		});
	}

	getMonthData(year: number, month: number): IPixelMapDay[] {
		let data;

		switch (year) {
			case 2023:
				switch (month) {
					case 0: data = january2023Data; break;
					case 1: data = february2023Data; break;
					case 2: data = march2023Data; break;
					case 3: data = april2023Data; break;
					case 4: data = may2023Data; break;
					case 5: data = june2023Data; break;
					case 6: data = july2023Data; break;
					case 7: data = august2023Data; break;
					case 8: data = september2023Data; break;
					case 9: data = october2023Data; break;
					case 10: data = november2023Data; break;
					case 11: data = december2023Data; break;
					default: throw new Error('Invalid month for year 2023');
				}
				break;

			case 2024:
				switch (month) {
					case 0: data = january2024Data; break;
					case 1: data = february2024Data; break;
					case 2: data = march2024Data; break;
					case 3: data = april2024Data; break;
					case 4: data = may2024Data; break;
					case 5: data = june2024Data; break;
					case 6: data = july2024Data; break;
					case 7: data = august2024Data; break;
					case 8: data = september2024Data; break;
					case 9: data = october2024Data; break;
					case 10: data = november2024Data; break;
					case 11: data = december2024Data; break;
					default: throw new Error('Invalid month for year 2024');
				}
				break;

			case 2025:
				switch (month) {
					case 0: data = january2025Data; break;
					case 1: data = february2025Data; break;
					case 2: data = march2025Data; break;
					case 3: data = april2025Data; break;
					case 4: data = may2025Data; break;
					case 5: data = june2025Data; break;
					case 6: data = july2025Data; break;
					case 7: data = august2025Data; break;
					case 8: data = september2025Data; break;
					case 9: data = october2025Data; break;
					case 10: data = november2025Data; break;
					case 11: data = december2025Data; break;
					default: throw new Error('Invalid month for year 2025');
				}
				break;

			case 2026:
				switch (month) {
					case 0: data = january2026Data; break;
					case 1: data = february2026Data; break;
					case 2: data = march2026Data; break;
					case 3: data = april2026Data; break;
					case 4: data = may2026Data; break;
					case 5: data = june2026Data; break;
					case 6: data = july2026Data; break;
					case 7: data = august2026Data; break;
					case 8: data = september2026Data; break;
					case 9: data = october2026Data; break;
					case 10: data = november2026Data; break;
					case 11: data = december2026Data; break;
					default: throw new Error('Invalid month for year 2026');
				}
				break;

			default:
				throw new Error('Year data not available');
		}

		return data;
	}

	fetchCalendarData(year: number): IPixelMapDay[][] {
		const data = this.yearDataMap[year];
		if (!data) throw new Error('Year data not available');
		return data;
	}
}
